import { useCallback, useRef, useState } from "react";

import { GetTaskBoardResponse, searchWorkflowTasks, SearchWorkflowTasksResponse } from "../../../../../service/query";
import { Status } from "../../../../../service/Shared";
import { useTaskPanelWorkflow } from "../../../../../templates/WorkflowTaskPanel";
import { Toast } from "../../../../../widget";
import { buildTaskByUuidRequest } from "../utils";

type WorkflowTaskNode = SearchWorkflowTasksResponse["results"][number];

export interface UseTasksDashboardTaskPanelArgs {
  /** Reloads the dashboard's own queries after the panel changes a task. */
  onTaskUpdated: () => void;
}

export interface UseTasksDashboardTaskPanelReturnData {
  /** Opens a task in the panel, once its row has been fetched. Wired to every view's task click. */
  openTask: (taskId: string) => void;
  /** The task the panel is editing. Undefined until it loads, and never while the panel is open. */
  selectedTask: WorkflowTaskNode | undefined;
  /** The task board the selected task belongs to — the panel reads its steps and access from it. */
  taskPanelWorkflow: GetTaskBoardResponse["results"][number] | undefined;
  showTaskPanel: boolean;
  /** Flag the panel watches to reload itself; flipped by `handleTaskPanelRefresh`. */
  taskPanelRefresh: boolean;
  handleTaskPanelRefresh: () => void;
  handleCloseTaskPanel: () => void;
}

/**
 * Opens a dashboard task in the shared `WorkflowTaskPanel` (KAN-9564). One surface for every view —
 * the List's task link, and the Kanban/Gantt clicks that KAN-9562/63 point at the same handler.
 *
 * The full task node is fetched on open rather than cached from the board pages: the panel's
 * mutations are optimistic-concurrency checked on `rowVersion`, so editing against a row cached
 * before someone else's change would fail the save. The org-wide feed spans workflows, so the task's
 * own board is loaded per selection by `useTaskPanelWorkflow` (a general task has none).
 */
export const useTasksDashboardTaskPanel = ({
  onTaskUpdated,
}: UseTasksDashboardTaskPanelArgs): UseTasksDashboardTaskPanelReturnData => {
  const [selectedTask, setSelectedTask] = useState<WorkflowTaskNode>();
  const [showTaskPanel, setShowTaskPanel] = useState(false);
  const [taskPanelRefresh, setTaskPanelRefresh] = useState(false);

  // Monotonic id of the current selection, so a slower response for a task the user has moved on
  // from can't replace the one now open.
  const openIdRef = useRef(0);

  const onWorkflowError = useCallback((): void => {
    Toast.error({ message: "Couldn't load the task's workflow - please try again later." });
  }, []);

  const taskPanelWorkflow = useTaskPanelWorkflow(selectedTask?.workflowUuid, onWorkflowError, taskPanelRefresh);

  const openTask = useCallback((taskId: string): void => {
    openIdRef.current += 1;
    const openId = openIdRef.current;

    /*
      Closed and cleared until the task is in hand. `WorkflowTaskPanel` derives its mode from the
      presence of a task (`resolveTaskPanelMode`), so an open panel with none is not a loading state —
      it is the *create* form, with the wrong controls and input that the arriving task would discard.
      Clearing the selection also covers the case where a panel is already open on another task.
    */
    setSelectedTask(undefined);
    setShowTaskPanel(false);

    searchWorkflowTasks(buildTaskByUuidRequest(taskId))
      .then((response) => {
        if (openId !== openIdRef.current) return;

        const task = response.status === Status.Success ? response.data?.results[0] : undefined;

        if (!task) {
          Toast.error({ message: "Couldn't open the task - please try again later." });
          return;
        }

        setSelectedTask(task);
        setShowTaskPanel(true);
      })
      .catch(() => {
        if (openId !== openIdRef.current) return;

        Toast.error({ message: "Couldn't open the task - please try again later." });
      });
  }, []);

  /*
    A change made in the panel reloads both sides: the panel's own data (so it shows the saved task)
    and the dashboard's queries — the group counts and the section rows are server-side, so a status
    change moves the task between sections and neither count is right until they refetch.
  */
  const handleTaskPanelRefresh = useCallback((): void => {
    setTaskPanelRefresh((prev) => !prev);
    onTaskUpdated();
  }, [onTaskUpdated]);

  const handleCloseTaskPanel = useCallback((): void => {
    openIdRef.current += 1;
    setSelectedTask(undefined);
    setShowTaskPanel(false);
  }, []);

  return {
    openTask,
    selectedTask,
    taskPanelWorkflow,
    showTaskPanel,
    taskPanelRefresh,
    handleTaskPanelRefresh,
    handleCloseTaskPanel,
  };
};
